import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { supabase } from '../services/supabaseClient'
import type { Session, User } from '@supabase/supabase-js'

export type AppRole = 'admin' | 'leader' | 'team_mate'

export interface Profile {
  id: string
  email: string
  display_name: string | null
  role: string | null // job title
  app_role: AppRole
  team_id: string | null
  consented: boolean
  created_at: string
}

interface AuthState {
  session: Session | null
  user: User | null
  profile: Profile | null
  isLoading: boolean
  isInitialised: boolean
  error: string | null
}

const initialState: AuthState = {
  session: null,
  user: null,
  profile: null,
  isLoading: false,
  isInitialised: false,
  error: null,
}

// Restore session on app load
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return { session: null, profile: null }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()

    return { session, profile: profiles as Profile | null }
  }
)

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      const isArchived = error.code === 'user_banned' || /banned/i.test(error.message)
      return rejectWithValue(isArchived ? 'User account archived' : error.message)
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .single()

    return { session: data.session, profile: profile as Profile }
  }
)

export const signup = createAsyncThunk(
  'auth/signup',
  async (
    { email, password, registrationCode }: { email: string; password: string; registrationCode: string },
    { rejectWithValue }
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { registration_code: registrationCode },
        emailRedirectTo: `${window.location.origin}/login`,
      },
    })
    if (error) {
      const msg = error.message
      if (msg.includes('registration_code_required')) {
        return rejectWithValue('A registration code is required to sign up.')
      }
      if (msg.includes('invalid_registration_code')) {
        return rejectWithValue('That registration code is invalid, expired, or already used.')
      }
      return rejectWithValue(msg)
    }
    if (!data.session) {
      return { session: null, profile: null, confirmationRequired: true }
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', data.user!.id)
      .single()

    return { session: data.session, profile: profile as Profile, confirmationRequired: false }
  }
)

export const logout = createAsyncThunk('auth/logout', async () => {
  await supabase.auth.signOut()
})

export const sendPasswordReset = createAsyncThunk(
  'auth/sendPasswordReset',
  async (email: string, { rejectWithValue }) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/set-password`,
    })
    if (error) return rejectWithValue(error.message)
  }
)

export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (newPassword: string, { rejectWithValue }) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) return rejectWithValue(error.message)
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setProfile(state, action: PayloadAction<Profile>) {
      state.profile = action.payload
    },
    clearError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // restoreSession
    builder
      .addCase(restoreSession.pending, (state) => {
        state.isLoading = true
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.session = action.payload.session
        state.user = action.payload.session?.user ?? null
        state.profile = action.payload.profile
        state.isLoading = false
        state.isInitialised = true
      })
      .addCase(restoreSession.rejected, (state) => {
        state.isLoading = false
        state.isInitialised = true
      })

    // login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.session = action.payload.session
        state.user = action.payload.session?.user ?? null
        state.profile = action.payload.profile
        state.isLoading = false
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // signup
    builder
      .addCase(signup.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.session = action.payload.session
        state.user = action.payload.session?.user ?? null
        state.profile = action.payload.profile
        state.isLoading = false
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // logout
    builder.addCase(logout.fulfilled, (state) => {
      state.session = null
      state.user = null
      state.profile = null
    })

    // sendPasswordReset
    builder
      .addCase(sendPasswordReset.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // updatePassword
    builder
      .addCase(updatePassword.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const { setProfile, clearError } = authSlice.actions
export default authSlice.reducer
